import { saveCsvToLocalFile } from '../../../utils/other/csvUtils';
import {
  parseContent,
  parseChartConfig,
  extractChartData,
} from './chatContentParsing';

export function escapeCsvCell(value) {
  const str = value == null ? '' : String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function rowsToCsv(headers, rows) {
  const lines = [];
  if (headers?.length) {
    lines.push(headers.map(escapeCsvCell).join(','));
  }
  (rows || []).forEach(row => {
    lines.push(row.map(escapeCsvCell).join(','));
  });
  return lines.join('\n');
}

export function formatCellForCsv(cell) {
  if (typeof cell === 'object' && cell !== null) {
    const parts = [];
    if (cell.text) parts.push(cell.text);
    if (cell.imageUrl) parts.push(cell.imageUrl);
    return parts.join(' ').trim();
  }
  return String(cell ?? '');
}

export function parsedTableToCsv(headers, rows) {
  const csvHeaders = headers || [];
  const csvRows = (rows || []).map(row =>
    row.map(formatCellForCsv)
  );
  return rowsToCsv(csvHeaders, csvRows);
}

export function markdownToCsv(markdown) {
  if (!markdown) return null;
  const tableBlock = parseContent(markdown).find(block => block.type === 'table');
  if (!tableBlock) return null;
  return parsedTableToCsv(tableBlock.headers, tableBlock.rows);
}

export function htmlTableToCsv(html) {
  if (!html || typeof document === 'undefined') return null;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const table = doc.querySelector('table');
  if (!table) return null;

  return domTableToCsv(table);
}

export function domTableToCsv(tableElement) {
  if (!tableElement) return null;

  const rows = Array.from(tableElement.querySelectorAll('tr'));
  if (!rows.length) return null;

  const csvRows = rows.map(row =>
    Array.from(row.querySelectorAll('th, td')).map(cell => cell.innerText.trim())
  );

  const [headers, ...bodyRows] = csvRows;
  const hasHeader = rows[0]?.querySelector('th');
  if (hasHeader) {
    return rowsToCsv(headers, bodyRows);
  }
  return rowsToCsv([], csvRows);
}

export function chartDataToCsv(data) {
  if (!data) return null;

  const { labels = [], datasets = [] } = data;

  if (labels.length && datasets.length) {
    const headers = ['Label', ...datasets.map((ds, i) => ds.label || `Series ${i + 1}`)];
    const rows = labels.map((label, i) => [
      label,
      ...datasets.map(ds => ds.data?.[i] ?? ''),
    ]);
    return rowsToCsv(headers, rows);
  }

  if (datasets.length) {
    const allPoints = datasets.flatMap((ds, dsIdx) =>
      (Array.isArray(ds.data) ? ds.data : []).map(point => {
        if (typeof point === 'object' && point !== null) {
          return { series: ds.label || `Series ${dsIdx + 1}`, ...point };
        }
        return { series: ds.label || `Series ${dsIdx + 1}`, value: point };
      })
    );

    if (!allPoints.length) return null;

    const headers = [...new Set(allPoints.flatMap(point => Object.keys(point)))];
    const rows = allPoints.map(point => headers.map(header => point[header] ?? ''));
    return rowsToCsv(headers, rows);
  }

  return null;
}

export function chartToCsv(chart) {
  const parsed = parseChartConfig(chart);
  const data = parsed?.config?.data || extractChartData(chart);
  return chartDataToCsv(data);
}

export function downloadCsv(csvData, fileName) {
  if (!csvData) {
    console.warn('No CSV data to download');
    return false;
  }
  saveCsvToLocalFile(csvData, fileName);
  return true;
}

export function exportMarkdownTableToCsv(markdown, fileName) {
  const csv = markdownToCsv(markdown);
  return downloadCsv(csv, fileName);
}

export function exportHtmlTableToCsv(html, fileName) {
  const csv = htmlTableToCsv(html);
  return downloadCsv(csv, fileName);
}

export function exportParsedTableToCsv(headers, rows, fileName) {
  const csv = parsedTableToCsv(headers, rows);
  return downloadCsv(csv, fileName);
}

export function exportChartToCsv(chart, fileName) {
  const csv = chartToCsv(chart);
  return downloadCsv(csv, fileName);
}
